"use client";
import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SimpleTabs, type SimpleTab } from "../SimpleTabs";
import { DataTable, type Column } from "../DataTable";
import { useERP, formatBDT, formatNum } from "@/lib/erp/store";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle2, XCircle } from "lucide-react";

export function ApprovalsModule() {
  const erp = useERP();
  const { toast } = useToast();
  const [tab, setTab] = React.useState("pending");
  const tabs: SimpleTab[] = [
    { value: "pending", label: "Pending", count: erp.approvals.filter(a => a.status === "Pending").length },
    { value: "history", label: "History", count: erp.approvals.filter(a => a.status !== "Pending").length },
  ];

  const pending = erp.approvals.filter(a => a.status === "Pending");
  const history = erp.approvals.filter(a => a.status !== "Pending");

  const decide = (id: string, decision: "Approved" | "Rejected") => {
    erp.approveEntity(id, "CEO Arif Rahman", decision, decision === "Rejected" ? "Rejected via approvals queue" : undefined);
    toast({ title: `Approval ${decision}`, description: `Cascade actions executed where applicable.` });
  };

  const cols: Column<typeof erp.approvals[number]>[] = [
    { key: "approvalNumber", header: "AP #", sortable: true, render: r => <span className="font-mono text-xs">{r.approvalNumber}</span> },
    { key: "entityType", header: "Type", sortable: true },
    { key: "requestedBy", header: "Requested By" },
    { key: "amount", header: "Amount", align: "right", sortable: true, render: r => formatBDT(r.amount) },
    { key: "priority", header: "Priority", render: r => <Badge variant={r.priority === "High" ? "destructive" : r.priority === "Medium" ? "secondary" : "outline"}>{r.priority}</Badge> },
    { key: "submittedAt", header: "Submitted", render: r => new Date(r.submittedAt).toLocaleString("en-BD") },
    { key: "status", header: "Status", render: r => <Badge variant={r.status === "Approved" ? "default" : r.status === "Rejected" ? "destructive" : "secondary"}>{r.status}</Badge> },
  ];

  const pendingCols = [...cols, {
    key: "actions", header: "Actions", align: "right" as const,
    render: (r: typeof erp.approvals[number]) => (
      <div className="flex justify-end gap-1" onClick={e => e.stopPropagation()}>
        <Button size="sm" variant="outline" onClick={() => decide(r.id, "Approved")}><CheckCircle2 className="size-3.5" /> Approve</Button>
        <Button size="sm" variant="outline" className="text-destructive" onClick={() => decide(r.id, "Rejected")}><XCircle className="size-3.5" /> Reject</Button>
      </div>
    ),
  }];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-xl font-semibold sm:text-2xl">Approvals</h1>
        <p className="text-muted-foreground mt-1 text-sm">Queue of pending approvals with cascade actions to underlying transactions.</p>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card><CardHeader><CardTitle className="text-muted-foreground text-xs font-medium uppercase tracking-wide">Pending</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-semibold text-destructive">{formatNum(pending.length)}</div></CardContent></Card>
        <Card><CardHeader><CardTitle className="text-muted-foreground text-xs font-medium uppercase tracking-wide">Approved</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-semibold text-primary">{formatNum(history.filter(h => h.status === "Approved").length)}</div></CardContent></Card>
        <Card><CardHeader><CardTitle className="text-muted-foreground text-xs font-medium uppercase tracking-wide">Rejected</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-semibold">{formatNum(history.filter(h => h.status === "Rejected").length)}</div></CardContent></Card>
        <Card><CardHeader><CardTitle className="text-muted-foreground text-xs font-medium uppercase tracking-wide">Pending Value</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-semibold">{formatBDT(pending.reduce((s, a) => s + a.amount, 0))}</div></CardContent></Card>
      </div>
      <SimpleTabs value={tab} onValueChange={setTab} tabs={tabs} />
      {tab === "pending" && (
        <Card>
          <CardHeader><CardTitle>Pending Approvals</CardTitle></CardHeader>
          <CardContent>
            <DataTable
              columns={pendingCols as Column<typeof erp.approvals[number]>[]}
              data={pending}
              rowKey={r => r.id}
              searchKeys={["approvalNumber", "entityType", "requestedBy"]}
            />
          </CardContent>
        </Card>
      )}
      {tab === "history" && (
        <Card>
          <CardHeader><CardTitle>Approval History</CardTitle></CardHeader>
          <CardContent>
            <DataTable
              columns={cols}
              data={history}
              rowKey={r => r.id}
              searchKeys={["approvalNumber", "entityType", "requestedBy"]}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
